/**
 * Robust search utilities for multi-field, case-insensitive searching across lists
 * Supports fuzzy, partial, and exact matching with keyboard-friendly shortcuts
 */

export interface SearchableItem {
  id: string;
  [key: string]: any;
}

/**
 * Resolve user IDs to names for searchable fields
 * Used to make fields like 'createdBy' searchable by creator name
 */
export const enrichWithResolvedFields = <T extends SearchableItem>(
  items: T[],
  fieldResolvers: Record<string, (value: any) => string>
): T[] => {
  return items.map(item => {
    const enriched = { ...item };
    for (const [field, resolver] of Object.entries(fieldResolvers)) {
      enriched[`${field}_resolved`] = resolver(item[field]);
    }
    return enriched;
  });
};

/**
 * Normalizes search query: lowercase, trim whitespace, remove special chars
 */
export const normalizeQuery = (query: string): string => {
  return query.toLowerCase().trim();
};

/**
 * Normalizes field value for comparison: lowercase, convert to string
 */
export const normalizeFieldValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value).toLowerCase();
};

/**
 * Check if a single word matches a field value
 * Supports: exact match, prefix match (e.g., "mum" matches "mumbai"), partial match
 */
export const matchesWord = (word: string, fieldValue: string): boolean => {
  if (!word || !fieldValue) return false;
  
  const normalized = normalizeFieldValue(fieldValue);
  
  // Exact match
  if (normalized === word) return true;
  
  // Prefix match (e.g., "mum" matches "mumbai")
  if (normalized.startsWith(word)) return true;
  
  // Word boundary match (e.g., "bank" matches "state bank of india")
  const wordBoundaryRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
  if (wordBoundaryRegex.test(normalized)) return true;
  
  return false;
};

/**
 * Check if item matches multiple keywords (all must match)
 * Each keyword can match any searchable field
 */
export const matchesAllKeywords = (
  item: SearchableItem,
  keywords: string[],
  searchableFields: string[]
): boolean => {
  return keywords.every(keyword => {
    return searchableFields.some(field => {
      const value = item[field];
      return matchesWord(keyword, value);
    });
  });
};

/**
 * Main search function - searches items by multiple keywords across multiple fields
 * @param query - Search query (e.g., "mumbai 9000")
 * @param items - Array of items to search
 * @param searchableFields - Fields to search in (e.g., ["name", "city", "phone"])
 * @returns Filtered items that match ALL keywords
 */
export const searchItems = <T extends SearchableItem>(
  query: string,
  items: T[],
  searchableFields: string[]
): T[] => {
  if (!query.trim()) return items;
  
  const normalized = normalizeQuery(query);
  const keywords = normalized.split(/\s+/).filter(k => k.length > 0);
  
  if (keywords.length === 0) return items;
  
  return items.filter(item =>
    matchesAllKeywords(item, keywords, searchableFields)
  );
};

/**
 * Advanced search for specific field patterns (e.g., phone:9876543210, city:mumbai)
 * Falls back to multi-field search if no pattern matches
 */
export const searchWithPatterns = <T extends SearchableItem>(
  query: string,
  items: T[],
  searchableFields: string[],
  fieldPatterns?: Record<string, string> // Maps pattern prefix to field name (e.g., { "phone:": "whatsappNumber" })
): T[] => {
  if (!query.trim()) return items;
  
  const normalized = normalizeQuery(query);
  
  // Check if query matches "field:value" pattern
  if (fieldPatterns) {
    for (const [prefix, fieldName] of Object.entries(fieldPatterns)) {
      if (normalized.startsWith(prefix)) {
        const value = normalized.substring(prefix.length).trim();
        return items.filter(item => matchesWord(value, item[fieldName]));
      }
    }
  }
  
  // Fall back to multi-field search
  return searchItems(query, items, searchableFields);
};

/**
 * Highlights matching parts in text for display
 * Used to show why a result matched
 */
export const highlightMatches = (text: string, query: string): string => {
  if (!query.trim()) return text;
  
  const normalized = normalizeQuery(query);
  const keywords = normalized.split(/\s+/).filter(k => k.length > 0);
  
  let highlighted = text;
  keywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
    highlighted = highlighted.replace(regex, match => `**${match}**`);
  });
  
  return highlighted;
};

/**
 * Group and sort search results by relevance
 * - Exact matches first
 * - Prefix matches second
 * - Partial matches last
 * - Favorites first within each group
 */
export const rankResults = <T extends SearchableItem>(
  items: T[],
  query: string,
  searchableFields: string[],
  favoriteIds?: string[]
): T[] => {
  if (!query.trim()) {
    // No search, just sort by favorites
    return items.sort((a, b) => {
      const aFav = favoriteIds?.includes(a.id) ? 1 : 0;
      const bFav = favoriteIds?.includes(b.id) ? 1 : 0;
      return bFav - aFav;
    });
  }
  
  const normalized = normalizeQuery(query);
  
  // Categorize results by match quality
  const exactMatches: T[] = [];
  const prefixMatches: T[] = [];
  const partialMatches: T[] = [];
  
  items.forEach(item => {
    let matchQuality: 'exact' | 'prefix' | 'partial' | 'none' = 'none';
    
    for (const field of searchableFields) {
      const value = normalizeFieldValue(item[field]);
      
      if (value === normalized) {
        matchQuality = 'exact';
        break;
      } else if (value.startsWith(normalized)) {
        if (matchQuality !== 'exact') matchQuality = 'prefix';
      } else if (value.includes(normalized)) {
        if (matchQuality !== 'exact' && matchQuality !== 'prefix') matchQuality = 'partial';
      }
    }
    
    if (matchQuality === 'exact') exactMatches.push(item);
    else if (matchQuality === 'prefix') prefixMatches.push(item);
    else if (matchQuality === 'partial') partialMatches.push(item);
  });
  
  // Sort each group by favorites first
  const sortByFavorites = (a: T, b: T) => {
    if (!favoriteIds) return 0;
    const aFav = favoriteIds.includes(a.id) ? 1 : 0;
    const bFav = favoriteIds.includes(b.id) ? 1 : 0;
    return bFav - aFav;
  };
  
  exactMatches.sort(sortByFavorites);
  prefixMatches.sort(sortByFavorites);
  partialMatches.sort(sortByFavorites);
  
  return [...exactMatches, ...prefixMatches, ...partialMatches];
};

/**
 * Create a hook-friendly search function with memoization support
 * Returns both filtered and ranked results
 */
export const createSearchFunction = <T extends SearchableItem>(
  searchableFields: string[],
  favoriteIds?: string[],
  fieldPatterns?: Record<string, string>
) => {
  return (query: string, items: T[]): T[] => {
    const results = fieldPatterns
      ? searchWithPatterns(query, items, searchableFields, fieldPatterns)
      : searchItems(query, items, searchableFields);
    
    return rankResults(results, query, searchableFields, favoriteIds);
  };
};
